import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import FormContainer from '../../components/form/FormContainer';
import Loader from '../../components/layout/Loader';
import Meta from '../../components/layout/Meta';
import KioskIdleCarousel from '../../components/product/KioskIdleCarousel';
import { useLazyGetProductContificoQuery } from '../../slices/contificoProductApiSlice';
import { useLazyGetProductDetailsByCtfIdQuery } from '../../slices/productApiSlice';
import '../../assets/styles/inStorePriceChecker.screen.css';

const InStorePriceCheckerScreen = () => {
    const IDLE_TIMEOUT_MS = 60_000;

    const [codigo, setCodigo] = useState('');
    const [product, setProduct] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [lastSearch, setLastSearch] = useState('');

    const inputRef = useRef(null);
    const inactivityTimeoutRef = useRef(null);
    const { userInfo } = useSelector((state) => state.auth);
    const { activeCompany } = useSelector((state) => state.admin);

    const [searchContificoProduct, { isFetching: isSearchingContifico }] =
        useLazyGetProductContificoQuery();
    const [getHomeSantyProduct, { isFetching: isFetchingHomeSanty }] =
        useLazyGetProductDetailsByCtfIdQuery();

    const isLoading = isSearchingContifico || isFetchingHomeSanty;

    const focusScannerInput = useCallback(() => {
        if (!userInfo?.isKiosk) {
            return;
        }

        window.requestAnimationFrame(() => {
            const input = inputRef.current;
            if (input && !input.disabled) {
                input.focus({ preventScroll: true });
            }
        });
    }, [userInfo?.isKiosk]);

    const encryptedDistPrice = (price) => {
        const [whole = '0', decimal = '00'] = String(price).split('.');
        const safeDecimal = decimal.padEnd(2, '0').slice(0, 2);
        return `9${whole}9${safeDecimal}9`;
    };

    const normalizedProduct = useMemo(() => {
        if (!product) {
            return null;
        }

        const iva = Number(product.porcentaje_iva || 0);
        const pvp1 = Number(product.pvp1 || 0);
        const pvp4 = Number(product.pvp4 || 0);
        const finalPvp = (pvp1 * (1 + iva / 100)).toFixed(2);
        const finalDistPvp = (pvp4 * (1 + iva / 100)).toFixed(2);

        return {
            ...product,
            finalPvp,
            encryptedDistPvp: encryptedDistPrice(finalDistPvp),
            stock: Number.parseInt(product.cantidad_stock || 0, 10),
        };
    }, [product]);

    useEffect(() => {
        focusScannerInput();
    }, [focusScannerInput]);

    useEffect(() => {
        if (isLoading) {
            return;
        }

        focusScannerInput();
    }, [isLoading, product, errorMessage, focusScannerInput]);

    const resetToIdle = useCallback(() => {
        setCodigo('');
        setProduct(null);
        setErrorMessage('');
        setLastSearch('');
        focusScannerInput();
    }, [focusScannerInput]);

    useEffect(() => {
        if (!userInfo?.isKiosk) {
            return undefined;
        }

        const resetInactivityTimer = () => {
            if (inactivityTimeoutRef.current) {
                clearTimeout(inactivityTimeoutRef.current);
            }

            inactivityTimeoutRef.current = setTimeout(() => {
                if (!isLoading) {
                    resetToIdle();
                }
            }, IDLE_TIMEOUT_MS);
        };

        const activityEvents = ['pointerdown', 'touchstart', 'keydown', 'input'];
        activityEvents.forEach((eventName) => {
            window.addEventListener(eventName, resetInactivityTimer);
        });

        resetInactivityTimer();

        return () => {
            if (inactivityTimeoutRef.current) {
                clearTimeout(inactivityTimeoutRef.current);
            }

            activityEvents.forEach((eventName) => {
                window.removeEventListener(eventName, resetInactivityTimer);
            });
        };
    }, [userInfo?.isKiosk, isLoading, resetToIdle]);

    const runContificoSearch = async (value) => {
        const company = activeCompany?.ruc;
        const searchByCode = await searchContificoProduct({
            codigo: value,
            ...(company ? { company } : {}),
        }).unwrap();
        if (Array.isArray(searchByCode) && searchByCode.length > 0) {
            return searchByCode;
        }

        const searchByBarcode = await searchContificoProduct({
            codigo_barra: value,
            ...(company ? { company } : {}),
        }).unwrap();
        return Array.isArray(searchByBarcode) ? searchByBarcode : [];
    };

    const submitHandler = async (event) => {
        event.preventDefault();

        const scannedCode = codigo.trim();
        if (!scannedCode) {
            setErrorMessage('Ingresa o escanea un codigo para consultar.');
            return;
        }

        setErrorMessage('');
        setLastSearch(scannedCode);

        try {
            const contificoResults = await runContificoSearch(scannedCode);

            if (!contificoResults.length) {
                setProduct([]);
                setCodigo('');
                return;
            }

            const contificoProduct = contificoResults[0];
            let enrichedImage = contificoProduct.imagen || '';

            if (contificoProduct?.id) {
                try {
                    const homeSantyProduct = await getHomeSantyProduct({
                        ctfid: contificoProduct.id,
                    }).unwrap();

                    if (homeSantyProduct?.image) {
                        enrichedImage = homeSantyProduct.image;
                    }
                } catch (imageError) {
                    // Keep Contifico image when the HomeSanty product does not exist yet.
                    console.log(imageError);
                }
            }

            setProduct({
                ...contificoProduct,
                imagen: enrichedImage,
            });
            setCodigo('');
        } catch (error) {
            console.log(error);
            setProduct(null);
            setCodigo('');
            setErrorMessage('No se pudo consultar el producto. Intenta nuevamente.');
        } finally {
            focusScannerInput();
        }
    };

    const clearResultHandler = () => {
        setProduct(null);
        setErrorMessage('');
        setCodigo('');
        setLastSearch('');
        focusScannerInput();
    };

    if (!userInfo?.isKiosk) {
        return (
            <FormContainer>
                <Meta title="Verificador de precios en tienda" />
                <Alert variant="warning" className="mt-4 mb-0">
                    Esta seccion esta disponible solo para usuarios de kiosco.
                </Alert>
            </FormContainer>
        );
    }

    return (
        <div className="in-store-price-checker-wrapper">
            <Meta title="Verificador de precios en tienda" />

            <Container fluid className="px-0">
                <div className="in-store-price-checker-stage">
                    <Card className="in-store-price-checker-card shadow-sm border-0">
                        <Card.Body className="p-3 p-md-4 p-lg-5">
                            <div className="in-store-header d-flex flex-wrap justify-content-center align-items-center gap-2 mb-4 text-center">
                                <div className="flex-grow-1">
                                    <h1 className="in-store-title mb-2">Verificador de Precios</h1>
                                    <p className="in-store-subtitle mb-0">
                                        Escanea o ingresa el código del producto
                                    </p>
                                </div>
                                {lastSearch && (
                                    <Badge bg="light" text="dark" className="px-3 py-2 border in-store-badge">
                                        {lastSearch}
                                    </Badge>
                                )}
                            </div>

                            <Form onSubmit={submitHandler} autoComplete="off">
                                <Row className="g-3 align-items-end">
                                    <Col xs={12} md={8}>
                                        <Form.Group controlId="codigo" className="mb-0">
                                            <Form.Label className="fw-semibold in-store-label" column="lg">
                                                Código o código de barras
                                            </Form.Label>
                                            <Form.Control
                                                ref={inputRef}
                                                type="text"
                                                inputMode="numeric"
                                                placeholder="Escanea o escribe el código"
                                                value={codigo}
                                                onChange={(e) => setCodigo(e.target.value)}
                                                className="in-store-input"
                                                disabled={isLoading}
                                                onBlur={() => {
                                                    if (!isLoading) {
                                                        focusScannerInput();
                                                    }
                                                }}
                                                autoFocus
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} md={4}>
                                        <div className="d-grid gap-2">
                                            <Button
                                                type="submit"
                                                className="in-store-btn-search"
                                                disabled={isLoading}
                                            >
                                                Buscar
                                            </Button>
                                            <Button
                                                type="button"
                                                className="in-store-btn-clear"
                                                onClick={clearResultHandler}
                                                disabled={isLoading && !product}
                                            >
                                                Limpiar
                                            </Button>
                                        </div>
                                    </Col>
                                </Row>
                            </Form>

                            {isLoading && (
                                <div className="text-center py-5 mt-4">
                                    <Loader />
                                    <p className="mt-3 mb-0 text-muted fw-semibold">Buscando producto...</p>
                                </div>
                            )}

                            {errorMessage && !isLoading && (
                                <Alert variant="danger" className="mt-4 mb-0 in-store-alert">
                                    <strong>Error:</strong> {errorMessage}
                                </Alert>
                            )}

                            {Array.isArray(product) && product.length === 0 && !isLoading && (
                                <Alert variant="warning" className="mt-4 mb-0 in-store-alert">
                                    <strong>No encontrado:</strong> No hay productos para el código ingresado.
                                </Alert>
                            )}

                            {/* ── Idle carousel — shown when no product is active ── */}
                            {!normalizedProduct && !isLoading && !errorMessage && !Array.isArray(product) && (
                                <KioskIdleCarousel />
                            )}

                            {/* ── Product result — supermarket kiosk layout ── */}
                            {normalizedProduct && !isLoading && (
                                <div className="in-store-result-card mt-4">
                                    <Row className="g-0 align-items-stretch">
                                        {/* LEFT: 3:4 portrait image */}
                                        <Col xs={12} md={5} lg={4} className="d-flex align-items-center justify-content-center in-store-result-image-col">
                                            <div className="in-store-image-portrait-wrap">
                                                <img
                                                    src={normalizedProduct.imagen || '/images/default.jpg'}
                                                    alt={normalizedProduct.nombre || 'Producto'}
                                                    className="in-store-product-image"
                                                    onError={(e) => {
                                                        e.target.src = '/images/default.jpg';
                                                    }}
                                                />
                                            </div>
                                        </Col>

                                        {/* RIGHT: product info */}
                                        <Col xs={12} md={7} lg={8} className="d-flex flex-column justify-content-center align-items-center in-store-result-info-col text-center">
                                            <h2 className="in-store-product-name mb-3">
                                                {normalizedProduct.nombre || 'Producto sin nombre'}
                                            </h2>

                                            <div className="in-store-product-codes mb-4">
                                                <p className="text-muted mb-1">
                                                    <span className="fw-semibold">Código:</span>{' '}
                                                    {normalizedProduct.codigo || 'N/A'}
                                                </p>
                                                <p className="text-muted mb-0">
                                                    <span className="fw-semibold">Código de barras:</span>{' '}
                                                    {normalizedProduct.codigo_barra || 'N/A'}
                                                </p>
                                            </div>

                                            <div className="in-store-price-block mb-4">
                                                <div className="in-store-price-label">PRECIO</div>
                                                <div className="in-store-price-main">
                                                    <span className="in-store-price-sym">$</span>
                                                    {normalizedProduct.finalPvp}
                                                </div>
                                                {/*<div className="in-store-price-label mt-3">Precio distribuidor</div>*/}
                                                <div className="in-store-price-dist">{normalizedProduct.encryptedDistPvp}</div>
                                            </div>

                                            <div>
                                                <Badge
                                                    bg={normalizedProduct.stock > 0 ? 'success' : 'secondary'}
                                                    className="px-4 py-2 in-store-stock-text"
                                                >
                                                    {normalizedProduct.stock > 0
                                                        ? `✓  ${normalizedProduct.stock} unidades disponibles`
                                                        : '✕  Sin stock'}
                                                </Badge>
                                            </div>
                                        </Col>
                                    </Row>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>
            </Container>
        </div>
    );
};

export default InStorePriceCheckerScreen;
